import { useQuery } from "@tanstack/react-query";

import { apiClient } from "../../lib/apiClient";

export interface MeUser {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
}

export interface ProjectListItem {
  id: string;
  name: string;
  has_icon: boolean;
  etag: string;
  /** Stored lifecycle status — "active" or "suspended". */
  status: "active" | "suspended";
  /** Registered players in the game. The team roster lives on the project page. */
  players_count: number;
  created_at: string;
  last_activity_at: string | null;
}

/**
 * Game-facing addresses of this deployment — what goes into a game client,
 * not the admin panel's own URL. Served by the backend from
 * SH_PUBLIC_REST_URL / SH_PUBLIC_WS_URL.
 */
export interface Endpoints {
  rest_base_url: string;
  websocket_url: string;
}

interface MeResponse {
  user: MeUser;
  is_last_super_admin: boolean;
  csrf_token: string;
  endpoints: Endpoints;
}

// Shared bootstrap query. Anything that needs a piece of /auth/me reuses this
// key so the payload is fetched once per staleTime, not once per consumer.
export const meQueryOptions = {
  queryKey: ["me"],
  queryFn: () =>
    apiClient.get<MeResponse>("/api/admin/auth/me").then((r) => {
      apiClient.setCSRF(r.data.csrf_token);
      return r.data;
    }),
  staleTime: 60_000,
};

/**
 * Public endpoints from the bootstrap payload. Undefined while /auth/me is in
 * flight or when it failed — callers render a placeholder in that case.
 */
export function useEndpoints(): Endpoints | undefined {
  return useQuery(meQueryOptions).data?.endpoints;
}

export function useAuth() {
  const me = useQuery(meQueryOptions);

  const projects = useQuery({
    queryKey: ["projects"],
    queryFn: () =>
      apiClient
        .get<{ projects: ProjectListItem[] }>("/api/admin/projects")
        .then((r) => r.data.projects),
    enabled: me.isSuccess,
    staleTime: 30_000,
  });

  return {
    user: me.data?.user,
    isLastSuperAdmin: me.data?.is_last_super_admin ?? false,
    projects: projects.data ?? [],
    loading: me.isLoading,
    error: me.error,
  };
}
